/**
 * SLA dashboard and alerting service.
 */
import apiClient from './apiClient';
import { API_URL } from '../config';

export type SLAWindow = '24h' | '7d' | '30d';

export interface SLAMetrics {
  module: string;
  total_polls: number;
  down_polls: number;
  degraded_or_down_polls: number;
  uptime_percent: number | null;
  error_rate_percent: number | null;
  avg_ms: number | null;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
}

export interface SLATargets {
  uptime_target_percent: number;
  response_time_p95_ms: number;
  error_rate_percent: number;
}

export interface SLAModuleCard {
  module: string;
  status: 'healthy' | 'degraded' | 'down';
  enabled: boolean;
  targets: SLATargets;
  metrics: SLAMetrics;
  breached: boolean;
  breach_reasons: string[];
}

export interface SLAAlertPayload {
  alert_id: string;
  type: string;
  severity: 'warning' | 'critical' | 'resolved';
  status: string;
  module: string;
  service: string;
  timestamp: string;
  current_status: string;
  message: string;
  recommended_action: string;
  acknowledgment_url: string;
  sla_metrics: SLAMetrics;
  sla_targets: Partial<SLATargets>;
}

export interface SLAAlertRecord extends SLAAlertPayload {
  id: number;
  status: 'firing' | 'acknowledged' | 'resolved';
  service_name: string;
  created_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  payload: SLAAlertPayload;
}

export interface SLADashboard {
  window: SLAWindow;
  overall_status: 'healthy' | 'degraded' | 'down';
  modules: SLAModuleCard[];
  active_alerts: SLAAlertRecord[];
  breach_history: SLAAlertRecord[];
}

const SLA_BASE = `${API_URL}/admin/sla`;

export async function fetchSLADashboard(window: SLAWindow = '24h'): Promise<SLADashboard> {
  const { data } = await apiClient.get(`${SLA_BASE}/dashboard`, { params: { window } });
  return data;
}

export async function fetchSLAAlerts(params?: {
  status?: 'firing' | 'acknowledged' | 'resolved' | 'all';
  severity?: 'warning' | 'critical' | 'all';
  service?: string;
  limit?: number;
}): Promise<SLAAlertRecord[]> {
  const { data } = await apiClient.get(`${SLA_BASE}/alerts`, { params });
  return data;
}

export async function acknowledgeSLAAlert(alertId: string, acknowledgedBy = 'admin'): Promise<SLAAlertRecord> {
  const { data } = await apiClient.post(`${SLA_BASE}/alerts/${alertId}/acknowledge`, {
    acknowledged_by: acknowledgedBy,
  });
  return data;
}

export async function createSilence(payload: {
  module: string;
  service_name?: string;
  duration_minutes: number;
  reason?: string;
}): Promise<unknown> {
  const { data } = await apiClient.post(`${SLA_BASE}/silences`, payload);
  return data;
}

export async function triggerSLAPoll(): Promise<unknown> {
  const { data } = await apiClient.post(`${SLA_BASE}/poll`);
  return data;
}

export async function fetchSLAReport(
  start?: string,
  end?: string,
  windowDays = 7,
  format: 'json' | 'csv' | 'pdf' = 'json'
): Promise<unknown> {
  const { data } = await apiClient.get(`${SLA_BASE}/report`, {
    params: { start, end, window_days: windowDays, format },
    responseType: format === 'json' ? 'json' : 'blob',
  });
  return data;
}

export function downloadBlob(blob: Blob, filename = 'download') {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  try {
    a.href = url;
    a.download = filename;
    a.click();
  } finally {
    URL.revokeObjectURL(url);
    a.remove();
  }
}
